import React from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import cls from 'classnames';
import { isAlwaysAvailable } from '../contracts/ContractPhase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComment,
    faEdit,
    faExclamationTriangle,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { toNumberSafe } from '../utilities/NumberHelpers';
import TimesheetContractPhaseModal from './TimesheetContractPhaseModal';
import TimesheetDetailBulkChange from './TimesheetDetailBulkChange';
import { isNoQuantityAllowed } from './CrewTimesheet';

export default class TimesheetContractPhaseButton extends React.Component {
    constructor(props) {
        super(props);

        this.state = { showModal: false };

        this.toggleModal = this.toggleModal.bind(this);
    }

    toggleModal() {
        if (isAlwaysAvailable(this.getContractPhase())) {
            return;
        }

        const { showModal } = this.state;

        this.setState({ showModal: !showModal });
    }

    getContractPhase() {
        const {
            job,
            timesheetContractPhase,
            timesheetContractPhaseAdjustment,
        } = this.props;

        return job.contract.contractPhases.find(
            (cp) =>
                timesheetContractPhase?.contractPhaseId == cp.id ||
                timesheetContractPhaseAdjustment?.contractPhaseId == cp.id
        );
    }

    getUnitOfMeasure() {
        const contractPhase = this.getContractPhase();

        return contractPhase.unitOfMeasure || '?';
    }

    getIsInvalid() {
        const {
            timesheetContractPhase,
            job,
            timesheetContractPhaseAdjustment,
        } = this.props;

        if (
            timesheetContractPhase?.notes ||
            timesheetContractPhaseAdjustment?.notes
        ) {
            return false;
        }

        const contractPhase = this.getContractPhase();

        if (
            isAlwaysAvailable(contractPhase) ||
            isNoQuantityAllowed(timesheetContractPhase)
        ) {
            return false;
        }

        if (timesheetContractPhaseAdjustment) {
            return (
                !timesheetContractPhaseAdjustment.quantity ||
                !timesheetContractPhaseAdjustment.timesheetPhaseMaterialAdjustments.every(
                    (tpma) => {
                        const contractPhase = job.contract.contractPhases.find(
                            (cp) =>
                                timesheetContractPhaseAdjustment.contractPhaseId ==
                                cp.id
                        );

                        const phaseMaterial = contractPhase.phaseMaterials.find(
                            (pm) => pm.id == tpma.phaseMaterialId
                        );

                        return (
                            tpma.quantity > 0 ||
                            !phaseMaterial ||
                            toNumberSafe(phaseMaterial.quantity) == 0
                        );
                    }
                )
            );
        }

        return (
            (!timesheetContractPhase.quantity ||
                !timesheetContractPhase.timesheetPhaseMaterials.every((tpm) => {
                    const contractPhase = job.contract.contractPhases.find(
                        (cp) => timesheetContractPhase.contractPhaseId == cp.id
                    );

                    const phaseMaterial = contractPhase.phaseMaterials.find(
                        (pm) => pm.id == tpm.phaseMaterialId
                    );

                    return (
                        tpm.quantity > 0 ||
                        !phaseMaterial ||
                        toNumberSafe(phaseMaterial.quantity) == 0
                    );
                })) &&
            !isAlwaysAvailable(contractPhase)
        );
    }

    canBeRemoved() {
        const { timesheetContractPhase, timesheetContractPhaseAdjustment } =
            this.props;

        if (timesheetContractPhaseAdjustment) {
            return (
                !timesheetContractPhaseAdjustment.quantity &&
                !timesheetContractPhaseAdjustment.timeSpent &&
                timesheetContractPhaseAdjustment.timesheetPhaseMaterialAdjustments.every(
                    (tpma) => !tpma.quantity
                )
            );
        }

        return (
            !timesheetContractPhase.targetQuantity &&
            !timesheetContractPhase.quantity &&
            !timesheetContractPhase.timeSpent &&
            timesheetContractPhase.timesheetPhaseMaterials.every(
                (tpm) => !tpm.quantity
            )
        );
    }

    isTimeAndQuantityMismatch() {
        const { timesheetContractPhaseAdjustment, timesheetContractPhase } =
            this.props;

        const contractPhase = this.getContractPhase();

        const quantityFreePhase = Boolean(
            isAlwaysAvailable(contractPhase) ||
            isNoQuantityAllowed(timesheetContractPhase)
        );

        if (timesheetContractPhaseAdjustment) {
            if (quantityFreePhase) {
                return (
                    !timesheetContractPhaseAdjustment.notes &&
                    Boolean(timesheetContractPhaseAdjustment.timeSpent <= 0)
                );
            }

            return (
                !timesheetContractPhaseAdjustment.notes &&
                Boolean(timesheetContractPhaseAdjustment.timeSpent > 0) !=
                    Boolean(
                        toNumberSafe(
                            timesheetContractPhaseAdjustment.quantity
                        ) > 0
                    )
            );
        }

        if (quantityFreePhase) {
            return (
                !timesheetContractPhase.notes &&
                Boolean(timesheetContractPhase.timeSpent <= 0)
            );
        }

        return (
            !timesheetContractPhase.notes &&
            Boolean(timesheetContractPhase.timeSpent > 0) !=
                Boolean(toNumberSafe(timesheetContractPhase.quantity) > 0)
        );
    }

    onContractPhaseRemove = (dataContainer) => {
        if (!dataContainer) {
            return;
        }

        const { contractPhaseId } = dataContainer;
        const { timesheet, onTimesheetMutated } = this.props;

        timesheet.timesheetContractPhases =
            timesheet.timesheetContractPhases.filter(
                (tcp) => tcp.contractPhaseId != contractPhaseId
            );

        timesheet.timesheetContractPhaseAdjustments =
            timesheet.timesheetContractPhaseAdjustments.filter(
                (tcpa) => tcpa.contractPhaseId != contractPhaseId
            );

        timesheet.timesheetDetails.forEach((td) => {
            td.timesheetDetailContractPhases =
                td.timesheetDetailContractPhases.filter(
                    (tdcp) => tdcp.contractPhaseId != contractPhaseId
                );

            td.timesheetDetailContractPhaseAdjustments =
                td.timesheetDetailContractPhaseAdjustments.filter(
                    (tdcpa) => tdcpa.contractPhaseId != contractPhaseId
                );
        });

        onTimesheetMutated(timesheet);
    };

    areAnyMaterialsUnset = (entityInQuestion) => {
        if (!entityInQuestion.quantity) {
            return false;
        }

        if (entityInQuestion.timesheetPhaseMaterialAdjustments) {
            return entityInQuestion.timesheetPhaseMaterialAdjustments.some(
                (tpma) => toNumberSafe(tpma.quantity) <= 0
            );
        }

        return entityInQuestion.timesheetPhaseMaterials.some(
            (tpm) => toNumberSafe(tpm.quantity) <= 0
        );
    };

    render() {
        const {
            loading,
            timesheet,
            timesheetContractPhase,
            timesheetContractPhaseAdjustment,
            isAdmin,
        } = this.props;

        if (
            loading ||
            !timesheet ||
            (!timesheetContractPhase && !timesheetContractPhaseAdjustment)
        ) {
            return null;
        }

        const contractPhase = this.getContractPhase();

        const entityInQuestion = timesheetContractPhaseAdjustment
            ? timesheetContractPhaseAdjustment
            : timesheetContractPhase;

        if (!contractPhase) {
            if (
                !timesheetContractPhase.quantity &&
                !timesheetContractPhase.timeSpent
            ) {
                return <div></div>;
            }

            return (
                <Card className="timesheet-contract-phase-card info-only">
                    <CardHeader>Hidden Phase</CardHeader>
                    <CardBody>
                        {timesheetContractPhase.quantity > 0 && (
                            <div>
                                {timesheetContractPhase.quantity} delivered.
                            </div>
                        )}
                        {timesheetContractPhase.timeSpent > 0 && (
                            <div>
                                {timesheetContractPhase.timeSpent} hours spent.
                            </div>
                        )}
                    </CardBody>
                </Card>
            );
        }

        const showTargetQuantity =
            timesheetContractPhase &&
            timesheetContractPhase.targetQuantity != 0 &&
            timesheetContractPhase.targetQuantity != undefined;

        const showFooter = Boolean(
            showTargetQuantity || entityInQuestion?.quantity
        );

        const isInvalid = this.getIsInvalid();
        const quantityFreePhase = isAlwaysAvailable(contractPhase);
        const unitOfMeasure = this.getUnitOfMeasure();

        const mismatchTimeAndQuantity = this.isTimeAndQuantityMismatch();

        const entityQuantity = toNumberSafe(entityInQuestion.quantity);
        const anyUnsetMaterials = this.areAnyMaterialsUnset(entityInQuestion);

        return (
            <div
                className="grid-col"
                key={`tcpb-${entityInQuestion.contractPhaseId}-${entityInQuestion.id}`}
            >
                <Card
                    className={cls('timesheet-contract-phase-card', {
                        'is-field-user-added':
                            isAdmin && entityInQuestion.isFieldUserAdded,
                    })}
                    onClick={this.toggleModal}
                >
                    <CardHeader
                        className={cls({
                            'always-available': quantityFreePhase,
                        })}
                    >
                        {contractPhase.displayPhase}

                        {entityInQuestion?.notes &&
                            entityInQuestion.notes.length > 0 && (
                                <FontAwesomeIcon
                                    icon={faComment}
                                    className="ml-1"
                                />
                            )}

                        {timesheetContractPhaseAdjustment && (
                            <FontAwesomeIcon icon={faEdit} className="ml-1" />
                        )}
                    </CardHeader>
                    <CardBody
                        className={cls({
                            'text-danger': isInvalid,
                        })}
                    >
                        {isInvalid && (
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="mr-1"
                            />
                        )}

                        {contractPhase.description}
                    </CardBody>

                    {showFooter && (
                        <CardFooter>
                            {showTargetQuantity && (
                                <div>
                                    Target: {entityInQuestion.targetQuantity}{' '}
                                    {unitOfMeasure}
                                </div>
                            )}

                            {entityQuantity != 0 && (
                                <div>
                                    Actual: {entityQuantity} {unitOfMeasure}
                                </div>
                            )}
                        </CardFooter>
                    )}

                    {anyUnsetMaterials && (
                        <CardFooter className="text-warning">
                            Check materials
                        </CardFooter>
                    )}

                    <CardFooter
                        className={cls({
                            'text-danger': mismatchTimeAndQuantity,
                        })}
                    >
                        {mismatchTimeAndQuantity && (
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="mr-1"
                            />
                        )}
                        {entityInQuestion.timeSpent} hrs
                    </CardFooter>
                </Card>

                {this.canBeRemoved() && (
                    <div className="text-center">
                        <Button
                            size="xs"
                            color="danger"
                            outline
                            onClick={this.onContractPhaseRemove.bind(this, {
                                contractPhaseId:
                                    entityInQuestion.contractPhaseId,
                            })}
                            className="mt-3"
                        >
                            <FontAwesomeIcon
                                icon={faTrashCan}
                                className="mr-1"
                            />
                            Remove
                        </Button>
                    </div>
                )}

                <TimesheetDetailBulkChange
                    {...this.props}
                    contractPhase={contractPhase}
                />

                <TimesheetContractPhaseModal
                    {...this.props}
                    {...this.state}
                    contractPhase={contractPhase}
                    unitOfMeasure={unitOfMeasure}
                    entityInQuestion={entityInQuestion}
                    onToggleModal={this.toggleModal}
                />
            </div>
        );
    }
}
